import { Box, SystemStyleObject, useColorModeValue } from '@chakra-ui/react';
import { flexRender, Header } from '@tanstack/react-table';
import { ForwardedRef, forwardRef, MouseEvent, TouchEvent, useCallback, useContext } from 'react';

import { TableContext } from './table-context';
import { useCommonPinningStyles } from './use-common-pinning-styles';

export const TableColumnHeader = forwardRef(
  (
    {
      header,
      resizeHandleColor,
      styles,
      updateColumnSizing,
      isReorderable,
    }: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      header: Header<any, any>;
      resizeHandleColor: string;
      styles?: SystemStyleObject;
      updateColumnSizing(): Promise<void>;
      isReorderable: boolean;
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { isOverflowing } = useContext(TableContext);
    const resizeBgColor = useColorModeValue('gray.100', 'gray.600');
    const isResizingColumn = header.column.getIsResizing();
    const isFirstColumn = header.column.getIsFirstColumn();
    const colSpan =
      header.column.getIsFirstColumn('left') || header.column.getIsLastColumn('right') ? 2 : 1;

    return (
      <Box
        key={header.id}
        role="columnheader"
        className="why-table--header"
        gridColumnEnd={`span ${colSpan}`}
        zIndex={isResizingColumn ? 1 : undefined}
        backgroundColor={isResizingColumn ? resizeBgColor : undefined}
        __css={styles}
        style={useCommonPinningStyles(header.column, isOverflowing)}
        ref={ref}
        ml={isFirstColumn ? (isReorderable ? 4 : 1) : 0}
      >
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {header.column.getCanResize() && (
          <TableColumnResizeHandle
            color={resizeHandleColor}
            header={header}
            updateColumnSizing={updateColumnSizing}
          />
        )}
      </Box>
    );
  },
);

export function TableColumnResizeHandle<TData, TValue>({
  color,
  header,
  updateColumnSizing,
}: {
  color: string;
  header: Header<TData, TValue>;
  updateColumnSizing(): Promise<void>;
}) {
  const onResize = useCallback(
    async (event: MouseEvent | TouchEvent) => {
      await updateColumnSizing();
      header.getResizeHandler()(event);
    },
    [header, updateColumnSizing],
  );

  return (
    <Box
      className="why-table--resize-handle"
      position="absolute"
      top="0"
      right="0"
      bottom="0"
      width="3px"
      cursor="col-resize"
      backgroundColor={header.column.getIsResizing() ? color : undefined}
      transition="background-color 0.2s ease-out"
      zIndex="1"
      onMouseDown={onResize}
      onTouchStart={onResize}
    />
  );
}
